import { useDatepicker, useMonth } from "@datepicker-react/hooks";
import dayjs from "dayjs";
import { AscCalendarEvent } from "../../../components/AscCalendarEvents/AscCalendarEvents.types";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);

export type CalendarDate = {
  dayLabel: string;
  date: Date;
  flag?: "previousMonth" | "activeMonth" | "nextMonth";
  events?: AscCalendarEvent[];
};

type UseDatepickerValuesOptions = Partial<
  Parameters<typeof useDatepicker>
>[0] extends undefined
  ? object
  : Partial<Parameters<typeof useDatepicker>[0]>;

interface UseCalendarDaysOptions extends UseDatepickerValuesOptions {
  events?: AscCalendarEvent[];
}

export function useCalendarDays(options?: UseCalendarDaysOptions) {
  const { events, numberOfMonths = 1, ...rest } = options || {};
  const {
    activeMonths,
    goToPreviousMonths,
    goToNextMonths,
    firstDayOfWeek = 0,
    ...restUseDatepickerValues
  } = useDatepicker({
    startDate: null,
    endDate: null,
    focusedInput: null,
    onDatesChange: () => null,
    numberOfMonths,
    ...rest,
  });

  const activeMonthDate = dayjs(
    `${activeMonths[0]?.year}/${activeMonths[0]?.month + 1}`,
    "YYYY/M"
  );

  const {
    days: activeMonthDays,
    weekdayLabels,
    upcomingEvents,
  } = useDaysOfActiveMonth(activeMonthDate, events);

  const previousMonthsDays = useDaysOfTheLastWeekOfThePreviousMonth(
    activeMonthDate,
    firstDayOfWeek
  );
  const nextMonthsDays = useDaysOfTheFirstWeekOfTheNextMonth(
    activeMonthDate,
    (firstDayOfWeek + 6) % 7
  );

  const calendarDaysForMonth: CalendarDate[] = [
    ...previousMonthsDays,
    ...activeMonthDays,
    ...nextMonthsDays,
  ];

  function getEventsByDate(date: string) {
    return (
      upcomingEvents?.filter((event) =>
        dayjs(date)?.isSame(event.date, "day")
      ) || []
    );
  }

  return {
    goToPreviousMonths,
    goToNextMonths,
    weekdayLabels,
    activeMonthLabel: activeMonthDate?.format("MMMM YYYY"),
    calendarDaysForMonth,
    upcomingEvents,
    getEventsByDate,
    activeMonths,
    firstDayOfWeek,
    ...restUseDatepickerValues,
  };
}

function useDaysOfActiveMonth(
  activeMonthDate: dayjs.Dayjs,
  events?: AscCalendarEvent[]
): {
  days: CalendarDate[];
  weekdayLabels: string[];
  upcomingEvents?: AscCalendarEvent[];
} {
  const { days, weekdayLabels } = useMonth({
    month: activeMonthDate.month(),
    year: activeMonthDate.year(),
    weekdayLabelFormat: (date) => dayjs(date).format("ddd"),
  });

  const activeMonthDays: CalendarDate[] = [];
  const upcomingEvents: AscCalendarEvent[] = [];
  for (const day of days) {
    if (typeof day !== "object") {
      continue;
    }

    const thisDaysEvents =
      events?.filter((event) => dayjs(day.date)?.isSame(event.date, "day")) ||
      [];

    activeMonthDays.push({
      ...(day as CalendarDate),
      flag: "activeMonth",
      events: thisDaysEvents,
    });
    if (dayjs().isSameOrBefore(dayjs(day.date))) {
      upcomingEvents.push(...thisDaysEvents);
    }
  }

  return {
    days: activeMonthDays,
    upcomingEvents,
    weekdayLabels,
  };
}

function useDaysOfTheLastWeekOfThePreviousMonth(
  activeMonthDate: dayjs.Dayjs,
  firstDayOfWeek = 0
): CalendarDate[] {
  const previousMonthDate = activeMonthDate.subtract(1, "month");

  const { days: previousMonthsDays } = useMonth({
    month: previousMonthDate.month(),
    year: previousMonthDate.year(),
  });
  const daysOfTheLastWeekOfThePreviousMonth: CalendarDate[] = [];
  for (let i = previousMonthsDays.length - 1; i >= 0; i--) {
    const day = previousMonthsDays[i];
    if (typeof day !== "object") {
      continue;
    }
    if (dayjs(day.date).day() === firstDayOfWeek) {
      daysOfTheLastWeekOfThePreviousMonth.unshift({
        ...day,
        flag: "previousMonth",
      });
      break;
    }
    daysOfTheLastWeekOfThePreviousMonth.unshift({
      ...day,
      flag: "previousMonth",
    });
  }

  return daysOfTheLastWeekOfThePreviousMonth?.length >= 7
    ? []
    : daysOfTheLastWeekOfThePreviousMonth;
}

function useDaysOfTheFirstWeekOfTheNextMonth(
  activeMonthDate: dayjs.Dayjs,
  lastDayOfWeek = 6
): CalendarDate[] {
  const nextMonthDate = activeMonthDate.add(1, "month");

  const { days: nextMonthsDays } = useMonth({
    month: nextMonthDate.month(),
    year: nextMonthDate.year(),
  });
  const daysOfTheLastWeekOfTheNextMonth: CalendarDate[] = [];
  for (let i = 0; i < nextMonthsDays.length; i++) {
    const day = nextMonthsDays[i];
    if (typeof day !== "object") {
      continue;
    }
    if (dayjs(day.date).day() === lastDayOfWeek) {
      daysOfTheLastWeekOfTheNextMonth.push({ ...day, flag: "nextMonth" });
      break;
    }
    daysOfTheLastWeekOfTheNextMonth.push({ ...day, flag: "nextMonth" });
  }

  return daysOfTheLastWeekOfTheNextMonth?.length >= 7
    ? []
    : daysOfTheLastWeekOfTheNextMonth;
}
